import type {
  MainNavItem,
  TournamentNavSection,
  TournamentSectionId,
} from "@/types/navigation";

export const MAIN_NAV_ITEMS: MainNavItem[] = [
  { id: "home", href: "/", labelKey: "nav.main.home", iconName: "home" },
  {
    id: "how",
    href: "/how-to",
    labelKey: "nav.main.how",
    iconName: "info",
  },
  {
    id: "create",
    href: "/start",
    labelKey: "nav.main.create",
    iconName: "plus",
  },
];

export const TOURNAMENT_NAV_SECTIONS: TournamentNavSection[] = [
  {
    id: "tower",
    hrefTemplate: "/tournaments/{slug}",
    labelKey: "nav.tournament.torni",
  },
  {
    id: "matches",
    hrefTemplate: "/tournaments/{slug}/matches",
    labelKey: "nav.tournament.ottelut",
  },
  {
    id: "chat",
    hrefTemplate: "/tournaments/{slug}/chat",
    labelKey: "nav.tournament.keskustelu",
    hasDynamicChildren: true,
  },
  {
    id: "about",
    hrefTemplate: "/tournaments/{slug}/about",
    labelKey: "nav.tournament.tietoja",
  },
  {
    id: "my-team",
    hrefTemplate: "/tournaments/{slug}/my-team",
    labelKey: "nav.tournament.omaJoukkue",
  },
  {
    id: "pool",
    hrefTemplate: "/tournaments/{slug}/pool",
    labelKey: "nav.tournament.vapaatPelaajat",
  },
  {
    id: "statistics",
    hrefTemplate: "/tournaments/{slug}/statistics",
    labelKey: "nav.tournament.tilastot",
  },
  {
    id: "contact",
    hrefTemplate: "/tournaments/{slug}/contact",
    labelKey: "nav.tournament.contact",
  },
];

/**
 * Sections that stay reachable once a tournament is archived. Everything else
 * (matches, chat, about, my-team, pool, contact) is hidden from nav and 404s on
 * its route — see the archived guards in the tournament sub-pages. Chat is
 * excluded because its data is moved to the archived* collections on archive.
 */
export const ARCHIVED_VISIBLE_SECTION_IDS: TournamentSectionId[] = [
  "tower",
  "statistics",
];

export function buildSectionHref(template: string, slug: string): string {
  return template.replace("{slug}", slug);
}

/**
 * Derives the active L1 section and L2 child from the current pathname.
 *
 * The chat hub maps three URL shapes to one L1 (`chat`):
 *   /tournaments/{slug}/chat                  → L1=chat, L2=null (tournament chat)
 *   /tournaments/{slug}/chat/team             → L1=chat, L2="team"
 *   /tournaments/{slug}/chat/match/{matchId}  → L1=chat, L2={matchId}
 *
 * The tournament root `/tournaments/{slug}` maps to L1="tower".
 */
export function parseActiveFromPathname(pathname: string | null): {
  activeL1: TournamentSectionId | null;
  activeL2: string | null;
} {
  if (!pathname) return { activeL1: null, activeL2: null };

  const match = pathname.match(/^\/tournaments\/[^/]+(?:\/(.*))?$/);
  if (!match) return { activeL1: null, activeL2: null };

  const rest = match[1] ?? "";
  if (rest === "") return { activeL1: "tower", activeL2: null };

  const parts = rest.split("/").filter(Boolean);
  const first = parts[0];

  switch (first) {
    case "matches":
      return { activeL1: "matches", activeL2: null };
    case "about":
      return { activeL1: "about", activeL2: null };
    case "my-team":
      return { activeL1: "my-team", activeL2: null };
    case "pool":
      return { activeL1: "pool", activeL2: null };
    case "statistics":
      return { activeL1: "statistics", activeL2: null };
    case "contact":
      return { activeL1: "contact", activeL2: null };
    case "chat": {
      // /chat → tournament chat; /chat/team → team; /chat/match/{id} → match id
      if (parts.length === 1) return { activeL1: "chat", activeL2: null };
      if (parts[1] === "team") return { activeL1: "chat", activeL2: "team" };
      if (parts[1] === "match" && parts[2]) {
        return { activeL1: "chat", activeL2: parts[2] };
      }
      return { activeL1: "chat", activeL2: null };
    }
    default:
      return { activeL1: null, activeL2: null };
  }
}
